<template>
    <!-- List view -->
    <div v-show="isPhaseListView">
        <div class="row">
            <div class="col text-h5 text-weight-medium">
                {{ `${asset ? asset.name : ''} - ` + $t('Timeline') }}
            </div>
        </div>
        <!-- Actions above panel -->
        <div id="phase-list" class="row q-col-gutter-sm q-my-sm">
            <div class="col-md-4 col-xs-9">
                <date-time-picker
                    v-model="datetimePickerEndDate"
                    :dense="true"
                    :label="$t('Show the last 12 hours data starting from')"
                    :readonly="true"
                />
            </div>
            <div class="col-md-2 col-xs-3">
                <q-btn
                    @click="
                        onRequest({
                            pagination: table.pagination
                        })
                    "
                    :label="$t('Apply')"
                    :class="{ 'full-width': $q.screen.lt.md }"
                    color="accent"
                    text-color="white"
                    style="height: 39px"
                />
            </div>
            <div class="col-md-6 col-xs-12">
                <div class="row" :class="{ 'float-right': !$q.screen.lt.md }">
                    <q-btn
                        id="merge-button"
                        @click="isMergeMode = true"
                        :label="$t('Merge')"
                        class="bg-white"
                        :class="
                            $q.screen.lt.md
                                ? 'col q-mb-sm q-mr-sm'
                                : 'full-height'
                        "
                        :disabled="
                            this.isLoading ||
                            isMergeMode ||
                            isBulkUpdateMode ||
                            $userCannot('merge', 'phases')
                        "
                        color="accent"
                        outline
                    />
                    <q-btn
                        id="bulk-update-button"
                        @click="isBulkUpdateMode = true"
                        :label="$t('Bulk Update')"
                        class="bg-white"
                        :class="
                            $q.screen.lt.md
                                ? 'col q-mb-sm q-mr-sm'
                                : 'full-height q-ml-sm'
                        "
                        :disabled="
                            this.isLoading ||
                            isMergeMode ||
                            isBulkUpdateMode ||
                            $userCannot('edit', 'phases')
                        "
                        color="accent"
                        outline
                    />
                    <q-btn
                        @click="fetchLatestList"
                        :label="$t('Refresh')"
                        class="bg-white"
                        :class="
                            $q.screen.lt.md
                                ? 'col-4 q-mb-sm'
                                : 'full-height q-ml-sm'
                        "
                        icon="fas fa-sync-alt"
                        text-color="accent"
                        outline
                        :disable="this.isLoading"
                    />
                    <!-- Dialog box for export phases starts -->
                    <export-phases
                        v-if="showDownloadPhaseDialog"
                        :headerText="$t('Download Phases')"
                        :assetSelection="'particularAsset'"
                        :assetId="assetId"
                        :facilityId="facilityId"
                        @close="showDownloadPhaseDialog = false"
                        :startDate="getEndDate"
                        :endDate="datetimePickerEndDate"
                    />
                    <!-- Dialog box for export phases ends -->
                    <q-btn
                        @click="showDownloadPhaseDialog = true"
                        :label="$t('Download')"
                        class="q-ml-sm q-mb-sm bg-white"
                        color="accent"
                        outline
                        :class="[
                            $q.screen.xs ? 'full-width' : '',
                            $q.screen.lt.lg ? 'half-width ' : ' q-mx-sm'
                        ]"
                    >
                        <q-tooltip>{{ $t('Download Phases') }}</q-tooltip>
                    </q-btn>
                </div>
            </div>
        </div>

        <!-- Banners -->
        <q-banner
            class="bg-white q-mb-sm border-grey"
            v-show="isMergeMode"
            inline-actions
            rounded
        >
            <template v-slot:avatar>
                <q-icon name="fa fa-info-circle" color="primary"></q-icon>
            </template>
            <div class="row items-center justify-between">
                {{
                    $t('Select the phases to merge. Click Confirm to proceed.')
                }}
                <div :class="$q.screen.lt.sm ? 'full-width q-mt-md' : ''">
                    <q-btn
                        id="confirm-merge-button"
                        @click="showMergeDialog"
                        :label="$t('Confirm')"
                        color="primary"
                        class="float-right"
                    />

                    <q-btn
                        @click="cancelMerge"
                        :label="$t('Cancel')"
                        color="accent"
                        class="q-mr-sm float-right bg-white"
                        outline
                    />
                </div>
            </div>
        </q-banner>

        <q-banner
            class="bg-white q-mb-sm border-grey"
            v-show="isBulkUpdateMode"
            inline-actions
            rounded
            :dense="$q.screen.lt.sm"
        >
            <template v-slot:avatar>
                <q-icon name="fa fa-info-circle" color="primary"></q-icon>
            </template>
            <div class="row items-center justify-between">
                {{
                    $t('Select the phases to update. Click Confirm to proceed')
                }}
                <div :class="$q.screen.lt.sm ? 'full-width q-mt-md' : ''">
                    <q-btn
                        id="confirm-bulk-update-button"
                        @click="showBulkUpdateDialog"
                        :label="$t('Confirm')"
                        color="primary"
                        class="float-right"
                    />

                    <q-btn
                        @click="cancelBulkUpdate"
                        :label="$t('Cancel')"
                        color="accent"
                        outline
                        class="q-mr-sm float-right bg-white"
                    />
                </div>
            </div>
        </q-banner>

        <div v-if="table.data.length === 0 && !isLoading">
            <div
                id="no-data-section"
                class="text-center bg-white border-grey q-pa-lg height-100"
            >
                <div class="text-h5">
                    <q-icon
                        name="fa fa-file-exclamation"
                        size="xl"
                        color="grey-4"
                    />
                    <div
                        class="q-mt-sm text-secondary text-capitalize"
                        id="no-data-text"
                    >
                        {{ $t('No data available') }}
                    </div>
                </div>
            </div>
        </div>
        <div v-else>
            <!-- Product Runs Graph -->
            <div class="bg-white graph-container q-mb-md">
                <product-runs-graph
                    v-if="!isLoading"
                    :product-runs="table.data"
                    :start-date="graphStartDate"
                    :end-date="graphEndDate"
                    :graph-period-in-hours="GRAPH_PERIOD_IN_HOURS"
                    :facilityUnitSystem="facility?.unit_system"
                    @previous="graphRequest('previous')"
                    @next="graphRequest('next')"
                    @phaseClick="onPhaseClick"
                />
                <div v-else-if="isLoading" class="text-center q-mb-md q-pt-md">
                    <q-spinner color="primary" size="3em" class="q-mt-md" />
                </div>
            </div>

            <div
                v-show="isTimezoneFeatureEnabled"
                class="row items-center q-mb-md"
            >
                <div class="col-md-6 col-xs-12">
                    {{
                        $t(
                            'Displaying data over a 12-hour period starting from {start}',
                            {
                                start: getMonthDayTime(
                                    this.infoTextEndDate,
                                    this.userLocale,
                                    this.timezone
                                )
                            }
                        )
                    }}
                </div>
                <div
                    v-if="$q.screen.gt.md"
                    class="col-md-6 col-xs-12 text-right"
                >
                    <span>{{ $t('Timezone') }}:</span>
                    <q-radio
                        v-model="timezone"
                        val="UTC"
                        label="UTC"
                        color="accent"
                        keep-color
                    />
                    <q-radio
                        v-model="timezone"
                        val="local"
                        :label="localTimezoneName"
                        color="accent"
                        keep-color
                    />
                </div>
            </div>
            <div v-if="isLoading" class="text-center q-mb-md q-pt-md">
                <q-spinner color="primary" size="3em" />
            </div>
            <div
                v-else
                v-for="productRun in table.data"
                :key="productRun.product"
            >
                <q-expansion-item
                    v-if="productRun.product_name"
                    v-model="productRun.expanded"
                    class="q-mt-md"
                    header-class="bg-white border-grey"
                    default-opened
                >
                    <template v-slot:header>
                        <div class="row expansion-header">
                            <div
                                class="col-xs-12 col-md-4 flex items-center no-wrap text-h6 text-bold q-pa-md"
                            >
                                <q-icon
                                    name="far fa-box"
                                    class="q-mr-sm"
                                    size="sm"
                                    color="grey"
                                >
                                    <q-tooltip
                                        anchor="bottom middle"
                                        self="top middle"
                                    >
                                        {{ $t('Product') }}
                                    </q-tooltip>
                                </q-icon>
                                <div class="ellipsis">
                                    <q-tooltip>
                                        {{ productRun.product_name }}
                                    </q-tooltip>
                                    {{ productRun.product_name }}
                                </div>
                            </div>
                            <div class="col-xs-12 col-md-8 q-pa-md">
                                <div class="row full-width">
                                    <div class="col-xs-12 col-sm-6">
                                        <div class="row">
                                            <div
                                                class="col-xs-12 col-sm-12 col-md-6"
                                            >
                                                <div class="phase-label">
                                                    {{ $t('Started at') }}
                                                </div>
                                                <div
                                                    class="flex items-center no-wrap add-margin-top"
                                                >
                                                    {{
                                                        getMonthDayTime(
                                                            productRun.start,
                                                            userLocale,
                                                            timezone
                                                        )
                                                    }}
                                                </div>
                                            </div>
                                            <div
                                                class="col-xs-12 col-sm-12 col-md-6"
                                            >
                                                <div
                                                    class="phase-label"
                                                    :class="{
                                                        'q-mt-md':
                                                            $q.screen.lt.md
                                                    }"
                                                >
                                                    {{ $t('Duration') }}
                                                </div>
                                                <div
                                                    class="flex items-center no-wrap"
                                                    :class="{
                                                        'add-margin-top':
                                                            productRun.end
                                                    }"
                                                >
                                                    {{
                                                        convertDurationToDHMS(
                                                            productRun.duration_s
                                                        )
                                                    }}

                                                    <q-chip
                                                        v-if="!productRun.end"
                                                        :color="
                                                            ASSET_STATUS_TO_STYLE[
                                                                productRun
                                                                    .phases[0]
                                                                    .phase
                                                            ]['color']
                                                        "
                                                        text-color="white"
                                                        size="sm"
                                                        :label="$t('Ongoing')"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div
                                        class="col-xs-12 col-sm-6"
                                        :class="{ 'q-mt-md': $q.screen.lt.sm }"
                                    >
                                        <div class="row">
                                            <div
                                                class="col-xs-12 col-sm-12 col-md-6"
                                            >
                                                <div
                                                    :class="{
                                                        'text-align-right':
                                                            $q.screen.gt.xs
                                                    }"
                                                >
                                                    <div class="phase-label">
                                                        {{
                                                            $t('Product Count')
                                                        }}
                                                    </div>
                                                    <div class="add-margin-top">
                                                        {{
                                                            formatProductionValue(
                                                                productRun.product_count,
                                                                userLocale
                                                            ) || '-'
                                                        }}
                                                    </div>
                                                </div>
                                            </div>
                                            <div
                                                class="col-xs-12 col-sm-12 col-md-6"
                                            >
                                                <div
                                                    :class="{
                                                        'text-align-right':
                                                            $q.screen.gt.xs
                                                    }"
                                                >
                                                    <div
                                                        class="phase-label"
                                                        :class="{
                                                            'q-mt-md':
                                                                $q.screen.lt.md
                                                        }"
                                                    >
                                                        {{
                                                            $t(
                                                                'Product Quantity'
                                                            )
                                                        }}
                                                    </div>
                                                    <div class="add-margin-top">
                                                        {{
                                                            formatRowProductQuantity(
                                                                productRun
                                                            ) || '-'
                                                        }}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <!-- Spacer used to align the content of the product run card with the shutdown phases -->
                        <div class="icon-spacer"></div>
                    </template>
                    <div v-if="$q.screen.gt.md">
                        <div class="phase-table">
                            <phases-table
                                :phases="productRun.phases"
                                :isMergeEnabled="isMergeMode"
                                :isBulkUpdateEnabled="isBulkUpdateMode"
                                :selectedTimezone="timezone"
                                :facilityUnitSystem="facility?.unit_system"
                                :assetId="asset?.id"
                                :hasProximitySensor="
                                    asset?.has_proximity_sensor || false
                                "
                                :highlightedPhaseId="highlightedPhaseId"
                                @edit="edit"
                                @split="showSplitDialog"
                                @refreshList="refreshList"
                                :descriptionMaxLength="descriptionMaxLength"
                                @showDescription="openDescriptionDialog($event)"
                            />
                        </div>
                    </div>
                    <div v-else>
                        <div v-for="phase in productRun.phases" :key="phase.id">
                            <phase-card
                                v-if="phase.phase !== 'Shutdown'"
                                :ref="`phase-card-${phase.id}`"
                                @return="onPhaseDetailReturn"
                                @refreshList="refreshList"
                                @showDescription="openDescriptionDialog($event)"
                                :facilityId="facilityId"
                                :assetId="assetId"
                                :phaseId="phase.id"
                                :phaseUUID="phase.uuid"
                                :phase="phase.phase"
                                :start="phase.start"
                                :end="phase.end === null ? null : phase.end"
                                :isSelected="phase.selected"
                                :isMergeMode="isMergeMode"
                                :isBulkUpdateMode="isBulkUpdateMode"
                                :status="phase.phase"
                                :cause="phase.cause"
                                :causeName="phase.cause_name"
                                :product="phase.product"
                                :productName="phase.product_name"
                                :description="phase.description"
                                :descriptionMaxLength="descriptionMaxLength"
                                :proximityReading="phase.proximity_reading"
                                :hasProximitySensor="
                                    asset?.has_proximity_sensor || false
                                "
                                :productDuration="
                                    convertDurationToDHMS(
                                        calcNoProductDuration(phase)
                                    )
                                "
                                :class="dynamicCardClass(phase.start)"
                                :phaseDuration="
                                    convertDurationToDHMS(phase.duration_s)
                                "
                                :productCount="
                                    formatProductionValue(
                                        phase.product_count,
                                        userLocale
                                    )
                                "
                                :productQuantity="
                                    formatRowProductQuantity(phase)
                                "
                                :isMajor="phase.is_major"
                                :isSplittable="isSplittablePhase(phase)"
                                :highlightedPhaseId="highlightedPhaseId"
                                @split="
                                    // check if phase is more than 3 seconds long & if it's current
                                    !isSplittablePhase(phase)
                                        ? // handle the disabled mode @click instead of using
                                          // :disabled, as :disabled will make q-tooltip to
                                          // not show up, so we do nothing @click in disabled.
                                          () => {}
                                        : showSplitDialog(phase)
                                "
                                @triggerSelected="
                                    !isMergeMode && !isBulkUpdateMode
                                        ? () => {}
                                        : (phase.selected = !phase.selected)
                                "
                                :phaseMedia="phase.media"
                                :hasSquareBorder="true"
                                :hasBorder="false"
                                class="card-borders"
                            />
                        </div>
                    </div>
                </q-expansion-item>
                <!-- Shutdown phase -->
                <shutdown-phase
                    v-else
                    v-for="phase in productRun.phases"
                    :key="phase.id"
                    :facilityId="parseInt(facilityId)"
                    :assetId="parseInt(assetId)"
                    :status="phase.phase"
                    :phaseId="phase.id"
                    :causeId="phase.cause"
                    :causeName="phase.cause_name"
                    :start="phase.start"
                    :end="phase.end || null"
                    :duration="convertDurationToDHMS(phase.duration_s)"
                    :isOngoing="!phase.end"
                    :description="phase.description"
                    :mediaIds="phase.media"
                    :disableEdit="
                        isBulkUpdateMode ||
                        isMergeMode ||
                        !$userCan('edit', 'phases') ||
                        !isPhaseEditable(phase.end)
                    "
                    :uneditableMessage="getUneditableErrorMessage(phase.end)"
                    :disableSplit="
                        $userCannot('split', 'phases') ||
                        !isSplittablePhase(phase) ||
                        isMergeMode ||
                        isBulkUpdateMode
                    "
                    :splitTextColor="
                        $userCan('split', 'phases') && isSplittablePhase(phase)
                            ? 'warning'
                            : 'grey'
                    "
                    :isMergeMode="isMergeMode"
                    :isBulkUpdateMode="isBulkUpdateMode"
                    :isSelected="phase.selected"
                    @edit="edit(phase)"
                    @split="showSplitDialog(phase)"
                    @refreshList="refreshList"
                    @showDescription="openDescriptionDialog($event)"
                    @triggerSelected="
                        !isMergeMode && !isBulkUpdateMode
                            ? () => {}
                            : (phase.selected = !phase.selected)
                    "
                />
            </div>
        </div>
    </div>

    <!-- Detail view -->
    <div v-if="!isPhaseListView" id="phase-detail">
        <phase-detail
            @return="onPhaseDetailReturn"
            @save="refreshList"
            :assetId="assetId"
            :phaseId="selectedPhase.id"
            :phaseUUID="selectedPhase.uuid"
            :start="selectedPhase.start"
            :end="selectedPhase.end"
            :status="selectedPhase.status"
            :causeId="selectedPhase.causeId"
            :productId="selectedPhase.productId"
            :causeName="selectedPhase.causeName"
            :productName="selectedPhase.productName"
            :description="selectedPhase.description"
            :proximityReading="selectedPhase.proximityReading"
            :hasProximitySensor="selectedPhase.hasProximitySensor"
            :isMajor="selectedPhase.is_major"
            :phaseMedia="selectedPhase.media"
        />
    </div>

    <!-- Dialogs -->
    <merge-phase-dialog
        v-if="isShowMergeDialog"
        @merge="onMerge"
        @close="isShowMergeDialog = false"
        @refresh="refreshList"
        :show="isShowMergeDialog"
        :assetId="assetId"
        :phases="selectedPhases"
    />

    <bulk-update-phase-dialog
        v-if="isShowBulkUpdateDialog"
        @bulkupdate="onBulkUpdate"
        @close="isShowBulkUpdateDialog = false"
        :show="isShowBulkUpdateDialog"
        :facilityId="facilityId"
        :assetId="assetId"
        :phases="selectedPhases"
    />

    <split-phase-dialog
        v-if="selectedPhase.id"
        @split="onSplit"
        @close="
            isShowSplitDialog = false;
            clearSelectedPhase();
        "
        :show="isShowSplitDialog"
        :id="selectedPhase.id"
        :start="selectedPhase.start"
        :end="selectedPhase.end"
        :status="selectedPhase.status"
    />

    <description-dialog
        v-if="showDescriptionDialog"
        @close="showDescriptionDialog = false"
        :showDialog="showDescriptionDialog"
        :description="descriptionDialogContent"
    />
</template>

<script>
import BreadcrumbsManager from '@mixins/BreadcrumbsManager.vue';
import UpdateURL from '@mixins/UpdateURL.vue';
import DateTimePicker from '@components/DateTimePicker.vue';
import PhaseDetail from '@pages/AssetTimeline/PhaseDetail.vue';
import {
    ASSET_STATUS_TO_STYLE,
    MIN_SECONDS_FOR_PHASE_SPLIT
} from '@models/assets.js';
import { PROXIMITY_SENSOR_LABELS } from '@models/devices.js';
import { convertToUnitSystem } from '@models/products.js';
import { getMonthDayTime } from '@models/timeformats.js';
import { convertDurationToDHMS } from '@utils/time.js';
import MergePhaseDialog from './MergePhaseDialog.vue';
import BulkUpdatePhaseDialog from './BulkUpdatePhaseDialog.vue';
import SplitPhaseDialog from './SplitPhaseDialog.vue';
import PhaseCard from '@components/PhaseCard.vue';
import ExportPhases from '@components/ExportPhases.vue';
import {
    isValidMerge,
    isValidBulkUpdate,
    fetchPhases,
    getFlattenedPhases
} from './phaseList.js';
import { formatProductionValue } from '@utils/numberFormatters.js';
import {
    isPhaseEditable,
    getUneditableErrorMessage
} from '@utils/phaseChecker.js';
import { isFeatureEnabled } from '@utils/featureFlags.js';
import { mapActions, mapState } from 'pinia';
import { useAuthStore } from '@stores/auth';
import { useFacilitiesStore } from '@stores/facilities';
import { useAssetsStore } from '@stores/assets';
import { calcNoProductDuration } from '@utils/events.js';
import { scrollToPhase as scrollToPhaseUtil } from '@utils/phaseScrolling.js';
import PhasesTable from './PhasesTable.vue';
import DescriptionDialog from '@components/DescriptionDialog.vue';
import ShutdownPhase from '@components/ShutdownPhase.vue';
import ProductRunsGraph from '@components/ProductRunsGraph.vue';

export default {
    name: 'AssetTimeline',
    components: {
        DateTimePicker,
        MergePhaseDialog,
        BulkUpdatePhaseDialog,
        SplitPhaseDialog,
        PhaseDetail,
        PhaseCard,
        ExportPhases,
        ProductRunsGraph,
        PhasesTable,
        DescriptionDialog,
        ShutdownPhase
    },
    beforeUnmount() {
        // Clear any active timeouts when component is destroyed
        if (this.highlightTimeoutId) {
            clearTimeout(this.highlightTimeoutId);
            this.highlightTimeoutId = null;
        }
    },
    mixins: [BreadcrumbsManager, UpdateURL],
    props: {
        assetId: {
            type: [Number, String],
            required: true
        },
        facilityId: {
            type: [Number, String],
            required: true
        }
    },
    async created() {
        this.setBreadcrumbs([
            {
                label: this.$t('Facilities'),
                route: {
                    name: 'facility_list'
                }
            },
            {
                label: this.facility?.name,
                route: {
                    name: 'facility_dashboard',
                    params: {
                        facilityId: this.facility?.id
                    }
                }
            },
            {
                label: this.asset?.name,
                route: {
                    name: 'asset_dashboard',
                    params: {
                        assetId: this.asset?.id
                    }
                }
            },
            {
                label: this.$t('Timeline')
            }
        ]);

        // initialize table columns
        this.table.columns = this.initTableColumns();

        // initialize date time picker with query params provided, otherwise datetime now
        if ('datetime' in this.$route.query) {
            this.datetimePickerEndDate = new Date(
                this.$route.query.datetime
            ).toISOString();
        } else {
            this.datetimePickerEndDate = new Date().toISOString();
        }

        // load phases in the table
        this.onRequest({
            pagination: this.table.pagination
        });
    },
    data() {
        return {
            // models
            ASSET_STATUS_TO_STYLE,
            PROXIMITY_SENSOR_LABELS,
            MIN_SECONDS_FOR_PHASE_SPLIT,

            // time formats
            timezone: 'local',
            localTimezoneName: Intl.DateTimeFormat().resolvedOptions().timeZone,

            // datepicker
            datetimePickerEndDate: null,

            // highlighted phase from graph click
            highlightedPhaseId: null,
            highlightTimeoutId: null, // Store timeout ID to clear it when needed

            // phase selected for edit or splitting
            selectedPhase: {
                id: null,
                uuid: null,
                start: null,
                end: null,
                status: '',
                causeId: null,
                productId: null,
                causeName: '',
                productName: '',
                proximityReading: null,
                hasProximitySensor: false,
                media: []
            },

            // toggle checkbox mode on q-table
            isMergeMode: false,
            isBulkUpdateMode: false,

            // toggle panel view
            isPhaseListView: true,

            // dialog visibility
            isShowMergeDialog: false,
            isShowBulkUpdateDialog: false,
            isShowSplitDialog: false,

            // q-table
            table: {
                data: [],
                columns: [],
                pagination: {
                    sortBy: 'start',
                    descending: true,
                    page: 1,
                    rowsPerPage: 1000,
                    rowsNumber: null
                }
            },
            isLoading: false,

            // url to fetch phases from (value is set on created/mount with onRequest method)
            url: '',

            // export phases dialog
            showDownloadPhaseDialog: false,

            // selectors for missing-cause-label missing cause
            causeSelectProps: {
                url: `assets/${this.assetId}/causes/`,
                label: this.$t('Cause'),
                dialogTitle: this.$t('Select a cause'),
                searchLabel: this.$t('Search cause'),
                selection: null
            },
            graphSelectedPhase: '',
            GRAPH_PERIOD_IN_HOURS: 12,
            infoTextEndDate: '',
            graphStartDate: '',
            graphEndDate: '',
            // description dialog
            descriptionMaxLength: 50,
            showDescriptionDialog: false,
            descriptionDialogContent: ''
        };
    },
    computed: {
        ...mapState(useAssetsStore, {
            getAsset: 'getAsset'
        }),
        ...mapState(useFacilitiesStore, {
            getFacility: 'getFacility'
        }),
        ...mapState(useAuthStore, {
            userLocale: 'getUserLocale'
        }),
        /**
         * Current asset.
         * @return {Object}
         */
        asset() {
            return this.getAsset(this.assetId);
        },
        /**
         * Current facility.
         * @return {Object}
         */
        facility() {
            return this.getFacility(this.facilityId);
        },
        /**
         * Get the start date for the phases to fetch.
         * @return {String} - this.datetimePickerEndDate minus GRAPH_PERIOD_IN_HOURS
         */
        getEndDate() {
            const date = new Date(this.datetimePickerEndDate);
            date.setHours(date.getHours() - this.GRAPH_PERIOD_IN_HOURS);
            return date.toISOString();
        },
        /**
         * Get all selected phases.
         * @return {Array} - list of phase ids
         */
        selectedPhases() {
            return this.table.data.flatMap(productRun =>
                productRun.phases.filter(phase => phase.selected)
            );
        },
        /**
         * Get all phases flattened with their product run IDs for quick lookup.
         * @return {Array} - flattened array of phases with productRunId
         */
        flattenedPhases() {
            return getFlattenedPhases(this.table.data);
        },
        /**
         * Verify the state of the MEDIA_UPLOAD feature flag
         * @return {Boolean}
         */
        isMediaUploadEnabled() {
            return this.isFeatureEnabled('MEDIA_UPLOAD');
        },
        /**
         * Verify the state of the ASSET_TIMELINE_TIMEZONE feature flag
         * @return {Boolean}
         */
        isTimezoneFeatureEnabled() {
            return this.isFeatureEnabled('ASSET_TIMELINE_TIMEZONE') || false;
        }
    },
    watch: {
        /**
         * Watch for changes in the MEDIA_UPLOAD feature flag and
         * rebuild the table columns accordingly so the Media column
         * is shown/hidden reactively (e.g. after page refresh when the
         * flag value becomes available).
         *
         * @param {boolean} newVal - Current value of the feature flag.
         */
        isMediaUploadEnabled: {
            immediate: true,
            handler(newVal) {
                // Re-initialize columns every time the flag changes.
                // Using a fresh array from initTableColumns preserves
                // the original column order and any later additions.
                this.table.columns = this.initTableColumns();
            }
        },
        /**
         * Watch for changes in highlightedPhaseId and scroll to the phase in mobile view.
         * @param {number|string|null} newPhaseId - The new highlighted phase ID
         */
        highlightedPhaseId: {
            handler(newPhaseId) {
                // Input validation for security
                if (newPhaseId === null || newPhaseId === undefined) {
                    return;
                }

                // Find the target phase
                const targetPhase = this.flattenedPhases.find(
                    phase => phase.id === newPhaseId
                );

                if (!targetPhase || !targetPhase.productRunId) {
                    return;
                }

                // Find the product run
                const targetProductRun = this.table.data.find(
                    run => run.id === targetPhase.productRunId
                );

                if (!targetProductRun) {
                    return;
                }

                const isAlreadyExpanded = targetProductRun.expanded;

                // Expand the target product run if not already expanded
                if (!isAlreadyExpanded) {
                    targetProductRun.expanded = true;
                }

                // Check screen size for proper view handling
                const isMobileView = this.$q.screen.lt.lg;

                // Scroll to phase in mobile view with simplified timing
                if (isMobileView) {
                    if (!isAlreadyExpanded) {
                        // Product run was just expanded, wait for DOM update before scrolling
                        this.$nextTick(() => {
                            setTimeout(() => {
                                this.scrollToPhaseCardMobile(newPhaseId);
                            }, 300);
                        });
                    } else {
                        // Product run was already expanded, scroll immediately
                        this.$nextTick(() => {
                            setTimeout(() => {
                                this.scrollToPhaseCardMobile(newPhaseId);
                            }, 100);
                        });
                    }
                }
                // The PhasesTable component handles scrolling in desktop view.
            }
        }
    },
    methods: {
        isPhaseEditable,
        getUneditableErrorMessage,
        formatProductionValue,
        convertDurationToDHMS,
        isFeatureEnabled,
        calcNoProductDuration,
        getMonthDayTime,
        ...mapActions(useAssetsStore, ['setAssetPhaseAction']),
        /**
         *  Initialize the table columns.
         * @return {Array} - list of table columns
         */
        initTableColumns() {
            const columns = [
                {
                    label: 'Product Run'
                },
                {
                    label: 'Start/End'
                },
                {
                    field: 'duration_s',
                    label: 'Duration'
                },
                {
                    field: 'product_count',
                    label: 'Product Count'
                },
                {
                    field: 'product_quantity',
                    label: 'Quantity'
                },
                {
                    field: 'description',
                    label: 'Description'
                }
            ];

            // add media column if feature flag is enabled
            if (this.isMediaUploadEnabled) {
                columns.push({
                    field: 'media',
                    label: 'Media'
                });
            }

            // add proximity sensor column if asset has one
            if (this.asset?.has_proximity_sensor) {
                columns.push({
                    field: 'proximity_reading',
                    label: 'Product on line?'
                });
            }

            columns.push({
                label: 'Actions'
            });
            return columns;
        },
        /** Unselect all rows */
        unselectAllRows() {
            this.table.data.forEach(row => {
                row.selected = false;
            });
        },
        /**
         * Cancel the merge operation
         */
        cancelMerge() {
            this.isMergeMode = false;
            this.unselectAllRows();
        },
        /**
         * Cancel the bulk update operation
         */
        cancelBulkUpdate() {
            this.isBulkUpdateMode = false;
            this.unselectAllRows();
        },
        /**
         * Fetch more phases from the server and update table.
         */
        async getPhases() {
            this.isLoading = true;

            this.table = await fetchPhases(this.url, this.table);

            this.isLoading = false;
        },
        /**
         * Handle request for the next or previous period.
         * @param {String} period
         */
        graphRequest(period) {
            this.graphSelectedPhase = '';
            const date = new Date(this.datetimePickerEndDate);
            if (period === 'previous') {
                date.setHours(date.getHours() - this.GRAPH_PERIOD_IN_HOURS);
            } else {
                date.setHours(date.getHours() + this.GRAPH_PERIOD_IN_HOURS);
            }
            this.datetimePickerEndDate = date.toISOString();
            this.onRequest({
                pagination: this.table.pagination
            });
        },
        /**
         * Handle phase click from the graph to highlight and scroll to the phase.
         * @param {Object} phase - The clicked phase object
         */
        onPhaseClick(phase) {
            if (phase && phase.id) {
                // Clear any existing timeout to prevent premature highlight removal
                if (this.highlightTimeoutId) {
                    clearTimeout(this.highlightTimeoutId);
                    this.highlightTimeoutId = null;
                }

                // Set the highlighted phase ID
                this.highlightedPhaseId = phase.id;

                // Set a new timeout to clear the highlight after 5 seconds
                this.highlightTimeoutId = setTimeout(() => {
                    this.highlightedPhaseId = null;
                    this.highlightTimeoutId = null;
                }, 5000);
            }
        },
        /**
         * Fetch data from the server. Triggered by any table sort/search/page changes.
         * @param {Object} props
         */
        async onRequest(props) {
            if (this.asset && !this.asset.has_proximity_sensor) {
                //  remove item from product list
                let proximityReadingColumnIndex = this.table.columns.findIndex(
                    column => column.field === 'proximity_reading'
                );
                if (proximityReadingColumnIndex > 0) {
                    this.table.columns.splice(proximityReadingColumnIndex, 1);
                }
            }
            // if end date is in the future, set it to now
            if (new Date(this.datetimePickerEndDate) > new Date()) {
                this.datetimePickerEndDate = new Date().toISOString();
            }
            // update pagination object
            this.table.pagination = props.pagination;

            // clear existing data
            this.table.data = [];

            // update url
            this.url = this.updateURL({
                baseURL: 'assetphases/',
                params: {
                    asset: this.assetId,
                    start__lte: this.datetimePickerEndDate,
                    // this endpoint might take a while to complete because
                    // it returns all the phases within the date range.
                    // do not queue more than 30 days to be safe
                    end__gte: this.getEndDate,
                    large_pagination: true,
                    page: this.table.pagination.page,
                    ordering: '-end',
                    show_production: true,
                    // controls whether to include media
                    show_media: true
                }
            });

            //

            // update graph end date for the info text
            this.infoTextEndDate = this.datetimePickerEndDate;

            // update start and end date for the graph.
            // this is needed to detach the graph period
            // from the current date and time selected
            this.graphStartDate = this.getEndDate;
            this.graphEndDate = this.datetimePickerEndDate;

            // fetch data from the server
            await this.getPhases();
        },
        /**
         * Set the selected phase on edit/split mode.
         * @param {Object}
         */
        selectPhase(phase) {
            this.selectedPhase['id'] = phase.id;
            this.selectedPhase['uuid'] = phase.uuid;
            this.selectedPhase['start'] = phase.start;
            this.selectedPhase['end'] = phase.end;
            this.selectedPhase['status'] = phase.phase;
            this.selectedPhase['causeId'] = phase.cause;
            this.selectedPhase['causeName'] = phase.cause_name;
            this.selectedPhase['productId'] = phase.product;
            this.selectedPhase['productName'] = phase.product_name;
            this.selectedPhase['description'] = phase.description;
            this.selectedPhase['is_major'] = phase.is_major;
            this.selectedPhase['proximityReading'] = phase.proximity_reading;
            this.selectedPhase['hasProximitySensor'] =
                phase.has_proximity_sensor;
            this.selectedPhase['media'] = phase.media;
        },
        /**
         * Reset selected phase states.
         */
        clearSelectedPhase() {
            this.selectedPhase = {
                id: null,
                start: null,
                end: null,
                status: '',
                causeId: null,
                productId: null,
                causeName: '',
                productName: '',
                proximityReading: null,
                hasProximitySensor: false
            };
        },
        /**
         * Reset selected phase states and refresh list
         * after phase detail return.
         */
        async onPhaseDetailReturn() {
            this.isPhaseListView = true;
            this.isShowSplitDialog = false;
            this.clearSelectedPhase();
            await this.refreshList();
        },
        /**
         * Edit a phase.
         * @param {Object} phase
         */
        edit(phase) {
            this.selectPhase(phase);
            this.isPhaseListView = false;
        },
        /**
         * Handle confirm merge.
         */
        showMergeDialog() {
            if (isValidMerge(this.selectedPhases)) {
                this.isShowMergeDialog = true;
            } else {
                // notify user
                if (this.selectedPhases.length <= 1) {
                    this.$notifyError(
                        this.$t('Select at least two phases to merge.')
                    );
                } else {
                    this.$notifyError(
                        this.$t('Please select consecutive phases to merge.')
                    );
                }
                //
            }
        },
        /**
         * Handle confirm bulk update.
         */
        showBulkUpdateDialog() {
            if (isValidBulkUpdate(this.selectedPhases)) {
                this.isShowBulkUpdateDialog = true;
            } else {
                // notify user
                if (this.selectedPhases.length < 1) {
                    this.$notifyError(
                        this.$t('Select at least one phase to update.')
                    );
                } else {
                    this.$notifyError(
                        this.$t('Please select phases to update.')
                    );
                }
            }
        },
        /**
         * Handle merge emit.
         */
        async onMerge() {
            // exit merge mode
            this.isShowMergeDialog = false;
            this.isMergeMode = false;

            // unselect merge rows
            this.unselectAllRows();

            // reload phase list
            await this.refreshList();
        },
        /**
         * Handle bulk update emit.
         */
        async onBulkUpdate() {
            // exit bulk update mode
            this.isShowBulkUpdateDialog = false;
            this.isBulkUpdateMode = false;
            // unselect merge rows
            this.unselectAllRows();
            // reload phase list
            await this.refreshList();
        },
        /**
         * Show split dialog.
         * @param {Object} phase - Phase to split.
         */
        showSplitDialog(phase) {
            this.selectPhase(phase);
            this.isShowSplitDialog = true;
        },
        /**
         * Handle split emit.
         */
        async onSplit() {
            this.isShowSplitDialog = false;
            this.clearSelectedPhase();
            await this.refreshList();
        },
        /**
         * Format the product quantity row.
         * @param {Object} row
         * @return {String} - formatted product quantity. Null if no product quantity.
         */
        formatRowProductQuantity(row) {
            if (row.product_quantity === null) {
                return null;
            }

            const { value: converted_value, unit: converted_unit } =
                convertToUnitSystem(
                    parseFloat(row.product_quantity),
                    row.unit_measure,
                    this.facility?.unit_system
                );

            const formattedValue = this.formatProductionValue(
                converted_value,
                this.userLocale
            );

            return this.$t('{value} {unit}', {
                value: formattedValue,
                unit: converted_unit
            });
        },
        /**
         * Fetch the latest phase list from the server according to the current date and time.
         */
        async fetchLatestList() {
            // set current date and time to "now"
            this.datetimePickerEndDate = new Date().toISOString();

            // reset pagination
            this.table.pagination.page = 1;

            // fetch data from the server
            await this.onRequest({
                pagination: this.table.pagination
            });
        },
        /**
         * Refetch the results from the server.
         */
        async refreshList() {
            // fetch data from the server
            await this.onRequest({
                pagination: this.table.pagination
            });

            // set latest phase in store
            const latestPhaseData = this.table.data[0];

            // check if there is a phase
            if (!latestPhaseData) {
                return;
            }

            const phaseObj = {
                id: latestPhaseData['id'],
                phaseUUID: latestPhaseData['uuid'],
                assetId: latestPhaseData['asset'],
                status: latestPhaseData['phase'],
                cause: latestPhaseData['cause'],
                currentProduct: latestPhaseData['product'],
                start: latestPhaseData['start']
            };
            this.setAssetPhaseAction(phaseObj);
        },
        /**
         * Determine if a phase is splittable.
         * @params {Object}
         * @return {Boolean}
         */
        isSplittablePhase(row) {
            // check if phase is more than MIN_SECONDS_FOR_PHASE_SPLIT seconds
            return row.duration_s > this.MIN_SECONDS_FOR_PHASE_SPLIT;
        },
        /**
         * Select/ deselect cause and products in table-select component
         * @param {Object} row - Event from table row.
         * @param {Object} props - Table selection props (causeTableSelect, productTableSelect, MissingCauseLabel)
         */
        select(row, props) {
            if (props.selection && props.selection.value === row.id) {
                props.selection = null;
            } else {
                props.selection = {
                    label: row.name,
                    value: row.id
                };
            }
        },
        /**
         * Select or deselect a phase based on the graph click.
         * @param {Object} - Start time of the phase and index.
         */
        toggleSelectedPhase({ phaseStart, index }) {
            if (this.graphSelectedPhase === phaseStart) {
                this.graphSelectedPhase = '';
            } else {
                this.graphSelectedPhase = phaseStart;
            }
            // scroll to the phase in the table
            this.$refs.phaseListRef.scrollTo(index, 'start-force');
        },
        /**
         * Add a class to the card corresponding to the phase selected in the graph.
         * @return {String}
         */
        dynamicCardClass(phaseStart) {
            if (this.isMergeMode || this.isBulkUpdateMode) {
                return 'cursor-pointer ';
            }
            if (phaseStart === this.graphSelectedPhase) {
                return 'bg-secondary';
            }
            return '';
        },
        /**
         * Open description dialog.
         * @param {String} description - The description to show.
         */
        openDescriptionDialog(description) {
            this.showDescriptionDialog = true;
            this.descriptionDialogContent = description;
        },
        /**
         * Scroll to a specific phase card in mobile view.
         * This function adjusts the viewport to bring the target phase card into view.
         * @param {number|string} phaseId - The phase ID to scroll to
         * @returns {void}
         */
        scrollToPhaseCardMobile(phaseId) {
            // Validate input
            if (phaseId === null || phaseId === undefined) {
                console.warn(
                    'Invalid phaseId provided to scrollToPhaseCardMobile'
                );
                return;
            }

            // Check if we have any data to work with
            if (!this.table.data || !this.table.data.length) {
                console.warn('No phases data available for scrolling');
                return;
            }

            try {
                // Pre-calculate flattened phases array for better performance
                const flattenedPhases = this.table.data.flatMap(
                    productRun => productRun.phases
                );

                // Create a wrapper function for $nextTick that satisfies ESLint
                const nextTickWrapper = callback => {
                    if (typeof callback === 'function') {
                        this.$nextTick(callback);
                    }
                };

                scrollToPhaseUtil({
                    phaseId,
                    phases: flattenedPhases,
                    $refs: this.$refs,
                    nextTickFn: nextTickWrapper,
                    scrollType: 'mobile-cards'
                });
            } catch (error) {
                console.error(
                    'Error preparing to scroll to phase card:',
                    error
                );
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.cursor-pointer {
    cursor: pointer;
}

.graph-container {
    width: 100%;
    height: 115px;
    border: 1px solid $grey-4;
    border-radius: 4px;
}
// remove padding on the expansion item
:deep(.q-item) {
    padding: 0 !important;
}
// remove hover effect on expansion item
:deep(.q-expansion-item) {
    .q-focus-helper {
        visibility: hidden;
    }
}

// Prevent the expansion icon from getting out of the card
:deep(.q-item__section) {
    min-width: 35px !important;
}

.card-borders {
    border-top: none;
    border-bottom: 1px solid $grey-4;
    border-left: 1px solid $grey-4;
    border-right: 1px solid $grey-4;
}

.text-align-right {
    width: 155px;
    text-align: right;
}

// Spacer used to align the content of the product run card with the shutdown phases
.icon-spacer {
    height: 16px;
    width: 128px;
}

// Preserve a small margin for the expansion icon
.expansion-header {
    width: calc(100% - 40px);
}

// Add top margin to align product run content with the ongoing chip
.add-margin-top {
    margin-top: 4px;
}
</style>
